import { useCallback } from 'react';

import { isEmpty } from '../../common/misc';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { SortDirection } from '../../common/types/Common';
import {
    GuidanceSessionSortField,
    ScheduledEventStartState,
} from '../types/GuidanceSessions';
import useSearchGuidanceSessions from './useSearchGuidanceSessions';

export const useGetGuidanceSessions = (
    sessionState: 'upcoming' | 'past' | 'all',
) => {
    const user = useAuthedUser();
    const { id: userId, membership } = user ?? {};
    const { id: membershipId = '' } = membership ?? {};

    const hasMembership = !isEmpty(membershipId);
    const enableUpcomingSessions =
        hasMembership &&
        (sessionState === 'upcoming' || sessionState === 'all');
    const enablePastSessions =
        hasMembership && (sessionState === 'past' || sessionState === 'all');

    const {
        data: upcomingSessions,
        isLoading: isLoadingUpcomingSessions,
        isFetching: isFetchingUpcomingSessions,
        refetch: refetchUpcomingSessions,
    } = useSearchGuidanceSessions(
        {
            userId,
            membershipId,
            state: ScheduledEventStartState.UPCOMING,
            sortField: GuidanceSessionSortField.START_TIME,
            sortDirection: SortDirection.ASC,
        },
        {
            enabled: enableUpcomingSessions,
            refetchOnWindowFocus: true,
        },
    );

    const {
        data: pastSessions,
        isLoading: isLoadingPastSessions,
        isFetching: isFetchingPastSessions,
        refetch: refetchPastSessions,
    } = useSearchGuidanceSessions(
        {
            userId,
            membershipId,
            state: ScheduledEventStartState.PAST,
            sortField: GuidanceSessionSortField.START_TIME,
            sortDirection: SortDirection.DESC,
        },
        {
            enabled: enablePastSessions,
            refetchOnWindowFocus: true,
        },
    );

    const refetchSessions = useCallback(() => {
        if (['all', 'upcoming'].includes(sessionState))
            void refetchUpcomingSessions();
        if (['all', 'past'].includes(sessionState)) void refetchPastSessions();
    }, [refetchUpcomingSessions, refetchPastSessions, sessionState]);

    const isLoadingSessions =
        sessionState === 'all'
            ? isLoadingUpcomingSessions || isLoadingPastSessions
            : sessionState === 'upcoming'
              ? isLoadingUpcomingSessions
              : isLoadingPastSessions;

    const isFetchingSessions =
        sessionState === 'all'
            ? isFetchingUpcomingSessions || isFetchingPastSessions
            : sessionState === 'upcoming'
              ? isFetchingUpcomingSessions
              : isFetchingPastSessions;

    return {
        upcomingSessions: upcomingSessions?.results,
        pastSessions: pastSessions?.results,
        refetchSessions,
        isLoadingSessions,
        isFetchingSessions,
    };
};
